<!-- UserGroupForm.vue -->
<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";
import api from "@/services/api";

// Store
import { useUserGroupStore } from "@/stores/useUserGroupStore";

// Componentes modais
import AddUserGroupForm from "./AddUserGroupForm.vue";
import EditUserGroupForm from "./EditUserGroupForm.vue";
import DeleteUserGroupForm from "./DeleteUserGroupForm.vue";
import UserGroupCardModal from "./UserGroupCardModal.vue";
import BatchDeleteUserGroupForm from "./BatchDeleteUserGroupForm.vue";
import SearchUserGroupInput from "./SearchUserGroupInput.vue";
import BulkCartesianForm from "./BulkCartesianForm.vue";

// Paginação
import PaginationButtons from "../Generics/PaginationButtons.vue";
import PaginationInfo from "../Generics/PaginationInfo.vue";

import UserGroupHelpModal from "./UserGroupHelpModal.vue";

const userGroupStore = useUserGroupStore();

// Estados de exibição dos modais
const showAddModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const showBatchDeleteModal = ref(false);

const showCartesianModal = ref(false);
const cartesianMode = ref("add");

const showCardModal = ref(false);
const selectedUserGroupId = ref(null);

// Modal de ajuda
const showHelpModal = ref(false);

// Paginação
const currentPage = ref(1);
const pageSize = ref(10);

// ✅ EVITA "FLASH" DE LISTA COMPLETA
const associationsToRender = ref([]);
const isTableReady = ref(false);
const isLoadingTable = ref(false);

// Lógica de Dropdowns e Seleção
const openDropdownId = ref(null);
const isBatchDropdownOpen = ref(false);
const batchDropdownRef = ref(null);

// ====================================================================
// ✅ TOAST PADRÃO (igual TextForm.vue) — 3000ms
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function handleChildNotify(payload) {
  if (!payload) return;

  if (payload.type === "success") {
    showSuccessToast(payload.message || "Operação realizada com sucesso.");
    return;
  }

  showErrorToast(payload.message || "Ocorreu um erro.");
}

async function handleAssociationChanged() {
  await handleAssociationsChanged({
    goToFirstPage: false,
    closeAdd: false,
    closeEdit: false,
    closeDelete: false,
    closeBatchDelete: false,
    closeCartesian: false,
    clearSelection: false,
  });
}

async function handleAssociationDeleted() {
  await handleAssociationsChanged({
    goToFirstPage: true,
    closeAdd: false,
    closeEdit: false,
    closeDelete: true,
    closeBatchDelete: true,
    closeCartesian: false,
    clearSelection: true,
  });
}
// ====================================================================

// ✅ aqui, por enquanto, todo vínculo é "editável"
function canEdit() {
  return true;
}
function canView() {
  return true;
}
function canDelete() {
  return true;
}

const isAllSelectedOnPage = computed({
  get: () => {
    const editableIdsOnPage = associationsToRender.value
      .filter((a) => canEdit(a))
      .map((a) => a.userGroupId);

    if (editableIdsOnPage.length === 0) return false;

    return editableIdsOnPage.every((id) =>
      userGroupStore.selectedAssociationIds.includes(id)
    );
  },
  set: (value) => {
    const editableIdsOnPage = associationsToRender.value
      .filter((a) => canEdit(a))
      .map((a) => a.userGroupId);

    const allIdsOnPage = associationsToRender.value.map((a) => a.userGroupId);

    if (value) {
      editableIdsOnPage.forEach((id) => {
        if (!userGroupStore.selectedAssociationIds.includes(id)) {
          userGroupStore.selectedAssociationIds.push(id);
        }
      });
    } else {
      userGroupStore.selectedAssociationIds =
        userGroupStore.selectedAssociationIds.filter(
          (id) => !allIdsOnPage.includes(id)
        );
    }
  },
});

// pelo menos um item "editável" na página
const anyEditableOnPage = computed(() =>
  associationsToRender.value.some((a) => canEdit(a))
);

// critério global de seleção para ações em lote
const canRemoveInBatch = computed(() => {
  return (userGroupStore.totalAssociationsGlobalCount ?? 0) > 0;
});

const canDeleteSelected = computed(() => {
  if (!Array.isArray(userGroupStore.selectedAssociationIds)) return false;
  return userGroupStore.selectedAssociationIds.length > 0;
});

function normalizeAvatarPath(path) {
  if (!path) return "";

  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  const base = api.defaults.baseURL.replace(/\/api$/, "").replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;

  return `${base}${cleanPath}`;
}

function getAvatarUrl(photoPath) {
  if (!photoPath) return "";
  return normalizeAvatarPath(photoPath);
}

function resolveAssociationUserPhotoPath(association) {
  if (!association) return null;

  return (
    association.userPhotoPath ||
    association.userPhotoUrl ||
    association.photoPath ||
    association.photoUrl ||
    association.user?.photoPath ||
    association.user?.photoUrl ||
    association.user_photo_path ||
    association.user_photo_url ||
    null
  );
}

function toggleRowDropdown(associationId) {
  openDropdownId.value =
    openDropdownId.value === associationId ? null : associationId;
}

function handleEdit(index) {
  indexSelector(index);
  showEditModal.value = true;
  openDropdownId.value = null;
}

function handleView(index) {
  handleOpenCard(index);
  openDropdownId.value = null;
}

function handleOpenCard(index) {
  const association = associationsToRender.value[index];
  if (!association) return;

  selectedUserGroupId.value = association.userGroupId;
  userGroupStore.setSelectedAssociation(association);
  showCardModal.value = true;
}

function handleDelete(index) {
  indexSelector(index);
  showDeleteModal.value = true;
  openDropdownId.value = null;
}

function toggleSelection(id) {
  userGroupStore.toggleAssociationSelection(id);
}

// Fetch de dados
async function fetchAssociations() {
  try {
    isLoadingTable.value = true;

    if (!isTableReady.value) associationsToRender.value = [];

    await userGroupStore.fetchAssociations(
      currentPage.value,
      pageSize.value,
      userGroupStore.associationSearchTerm
    );

    associationsToRender.value = Array.isArray(userGroupStore.associations)
      ? [...userGroupStore.associations]
      : [];

    isTableReady.value = true;

    if (userGroupStore.shouldForcePageOne) {
      userGroupStore.shouldForcePageOne = false;

      if (currentPage.value !== 1) {
        currentPage.value = 1;
      } else {
        await userGroupStore.fetchAssociations(
          1,
          pageSize.value,
          userGroupStore.associationSearchTerm
        );

        associationsToRender.value = Array.isArray(userGroupStore.associations)
          ? [...userGroupStore.associations]
          : [];
      }
    }
  } catch (error) {
    console.error("❌ Erro ao buscar associações:", error?.message);
    showErrorToast("Erro ao carregar vínculos Pessoa-Grupo.");
  } finally {
    isLoadingTable.value = false;
  }
}

watch(currentPage, fetchAssociations);

watch(
  () => userGroupStore.associationSearchTerm,
  () => {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
    } else {
      fetchAssociations();
    }
  }
);

watch(
  () => userGroupStore.associations,
  (val) => {
    if (!isTableReady.value) return;
    associationsToRender.value = Array.isArray(val) ? [...val] : [];
  }
);

// ====================================================================
// ✅ Handler único pós-mudança (padrão TextForm.vue)
// ====================================================================
async function handleAssociationsChanged(options = {}) {
  const {
    goToFirstPage = true,
    closeAdd = true,
    closeEdit = true,
    closeDelete = true,
    closeBatchDelete = true,
    closeCartesian = true,
    clearSelection = false,
    successMessage = null,
  } = options;

  if (closeAdd) showAddModal.value = false;
  if (closeEdit) showEditModal.value = false;
  if (closeDelete) showDeleteModal.value = false;
  if (closeBatchDelete) showBatchDeleteModal.value = false;
  if (closeCartesian) showCartesianModal.value = false;

  if (clearSelection) userGroupStore.selectedAssociationIds = [];

  if (successMessage) showSuccessToast(successMessage);

  if (goToFirstPage) {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
      return;
    }
    await fetchAssociations();
    return;
  }

  await fetchAssociations();
}
// ====================================================================

function tryOpenBatchDeleteModal() {
  const selectedIds = userGroupStore.selectedAssociationIds;

  isBatchDropdownOpen.value = false;

  if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
    showErrorToast("Nenhum vínculo Pessoa-Grupo selecionado.");
    return;
  }

  showBatchDeleteModal.value = true;
}

function handleClickOutside(event) {
  if (
    batchDropdownRef.value &&
    !batchDropdownRef.value.contains(event.target)
  ) {
    isBatchDropdownOpen.value = false;
  }
  if (openDropdownId.value && !event.target.closest(".action-cell-container")) {
    openDropdownId.value = null;
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  fetchAssociations();
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);

  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);

  userGroupStore.associationSearchTerm = "";
  userGroupStore.currentPage = 1;
  userGroupStore.shouldForcePageOne = true;

  userGroupStore.selectedAssociationIds = [];
  userGroupStore.selectedAssociation = null;
  openDropdownId.value = null;
  isBatchDropdownOpen.value = false;
  selectedUserGroupId.value = null;

  showAddModal.value = false;
  showEditModal.value = false;
  showDeleteModal.value = false;
  showBatchDeleteModal.value = false;
  showCartesianModal.value = false;
  showCardModal.value = false;
  showHelpModal.value = false;
  cartesianMode.value = "add";

  associationsToRender.value = [];
  isTableReady.value = false;
  isLoadingTable.value = false;
});

function indexSelector(index) {
  const association = associationsToRender.value[index];
  if (!association) return;
  userGroupStore.setSelectedAssociation(association);
}

function openCartesianAddModal() {
  cartesianMode.value = "add";
  showCartesianModal.value = true;
  isBatchDropdownOpen.value = false;
}

function openCartesianRemoveModal() {
  cartesianMode.value = "remove";
  showCartesianModal.value = true;
  isBatchDropdownOpen.value = false;
}

async function handleCartesianSubmit(payload) {
  try {
    if (cartesianMode.value === "add") {
      await userGroupStore.bulkAddByCartesian(payload);
      await handleAssociationsChanged({
        goToFirstPage: true,
        closeCartesian: true,
        clearSelection: true,
        successMessage: "Vínculos adicionados com sucesso!",
      });
      return;
    }

    await userGroupStore.bulkRemoveByCartesian(payload);
    await handleAssociationsChanged({
      goToFirstPage: true,
      closeCartesian: true,
      clearSelection: true,
      successMessage: "Vínculos removidos com sucesso!",
    });
  } catch (error) {
    console.error("❌ Erro ao processar operação em lote:", error?.message);
    showErrorToast("Falha ao processar a operação em lote.");
    showCartesianModal.value = false;
    await fetchAssociations();
  }
}
</script>

<template>
  <div class="w-full h-full">
    <div class="main-container">
      <div class="main-header">
        <div class="flex flex-col gap-0">
          <div class="text-xl font-bold">Vínculos Pessoa-Grupo</div>
          <div class="text-sm mt-1 text-gray-700 ">
            Adicione pessoas a grupos e conceda permissões de acesso a textos,
            atividades e notas para os grupos de uma só vez.
            <button
              class="underline text-blue-600 hover:text-blue-800 ml-1"
              @click="showHelpModal = true"
            >
              Saiba mais
            </button>
          </div>
        </div>
      </div>

      <div class="action-bar">
        <div class="w-full md:w-1/2">
          <SearchUserGroupInput />
        </div>

        <div
          class="w-full md:w-auto flex flex-col md:flex-row space-y-2 md:space-y-0 items-stretch md:items-center justify-end md:space-x-3 flex-shrink-0"
        >
          <button @click="showAddModal = true" class="btn btn-primary">
            <svg class="h-4 w-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path
                clip-rule="evenodd"
                fill-rule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              />
            </svg>
            Adicionar Pessoa a Grupo
          </button>

          <div ref="batchDropdownRef" class="relative">
            <button
              @click="isBatchDropdownOpen = !isBatchDropdownOpen"
              class="btn btn-batch w-full"
            >
              Ações em lote
              <svg
                class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                :class="{ open: isBatchDropdownOpen }"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  clip-rule="evenodd"
                  fill-rule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                />
              </svg>
            </button>

            <div v-if="isBatchDropdownOpen" class="dropdown-menu">
              <div class="py-1">
                <a
                  href="#"
                  role="button"
                  @click.prevent="openCartesianAddModal"
                  class="dropdown-item"
                >
                  <i class="fas fa-plus-circle"></i>
                  <span>Adicionar em Lote</span>
                </a>

                <a
                  href="#"
                  role="button"
                  @click.prevent="
                    canRemoveInBatch && openCartesianRemoveModal()
                  "
                  class="dropdown-item danger-item"
                  :class="{ 'disabled-item': !canRemoveInBatch }"
                >
                  <i class="fas fa-minus-circle"></i>
                  <span>Remover em Lote</span>
                </a>

                <div class="border-t border-gray-200 my-1"></div>

                <a
                  href="#"
                  role="button"
                  @click.prevent="
                    canDeleteSelected && tryOpenBatchDeleteModal()
                  "
                  class="dropdown-item danger-item"
                  :class="{ 'disabled-item': !canDeleteSelected }"
                >
                  <i class="fas fa-trash-alt"></i>
                  <span>Apagar Selecionados</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="table-wrapper">
        <table class="main-table">
          <thead>
            <tr>
              <th style="width: 5.5%">
                <input
                  type="checkbox"
                  v-model="isAllSelectedOnPage"
                  class="checkbox-style"
                  :disabled="!anyEditableOnPage"
                  :class="{ 'disabled-item': !anyEditableOnPage }"
                />
              </th>
              <th style="width: 100px">Ações</th>
              <th style="width: 10%">ID Vínculo</th>
              <th style="width: 27%">Nome do Usuário</th>
              <th style="width: 20%">Email do Usuário</th>
              <th style="width: 31%">Nome do Grupo</th>
              <th style="width: 12%">Criador</th>
              <th style="width: 20%">Adicionado Em</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(association, index) in !isLoadingTable
                ? associationsToRender
                : []"
              :key="association.userGroupId"
              @click="handleOpenCard(index)"
              class="clickable-row"
            >
              <td @click.stop>
                <input
                  :class="{ 'checkbox-disabled': !canEdit(association) }"
                  type="checkbox"
                  :value="association.userGroupId"
                  :checked="
                    userGroupStore.selectedAssociationIds?.includes(
                      association.userGroupId
                    )
                  "
                  @change="toggleSelection(association.userGroupId)"
                  class="checkbox-style"
                  :disabled="!canEdit(association)"
                />
              </td>

              <td @click.stop class="action-cell-container">
                <div class="row-actions">
                  <button
                    @click="toggleRowDropdown(association.userGroupId)"
                    class="btn-icon btn-soft-brand"
                    title="Menu de Ações"
                  >
                    <i class="fas fa-ellipsis-h fa-lg"></i>
                  </button>
                </div>

                <div
                  v-if="openDropdownId === association.userGroupId"
                  class="row-dropdown-menu"
                >
                  <ul>
                    <li
                      :class="{ 'disabled-item': !canEdit(association) }"
                      @click="canEdit(association) && handleEdit(index)"
                    >
                      <i class="fas fa-pencil-alt"></i><span>Editar</span>
                    </li>
                    <li
                      :class="{ 'disabled-item': !canView(association) }"
                      @click="canView(association) && handleView(index)"
                    >
                      <i class="fas fa-eye"></i><span>Visualizar</span>
                    </li>
                    <li
                      class="danger-item"
                      :class="{ 'disabled-item': !canDelete(association) }"
                      @click="canDelete(association) && handleDelete(index)"
                    >
                      <i class="fas fa-trash-alt"></i><span>Apagar</span>
                    </li>
                  </ul>
                </div>
              </td>

              <td>
                <span>{{ association.userGroupId }}</span>
              </td>
              <td class="cell-text-center">
                <span class="user-name-cell">
                  <span class="user-avatar">
                    <span
                      v-if="
                        getAvatarUrl(
                          resolveAssociationUserPhotoPath(association)
                        )
                      "
                      class="user-avatar-img"
                      :style="{
                        backgroundImage: `url('${getAvatarUrl(
                          resolveAssociationUserPhotoPath(association)
                        )}')`,
                      }"
                    ></span>

                    <i v-else class="fas fa-user user-avatar-icon"></i>
                  </span>

                  <span class="user-name-text">
                    {{ association.userName || "—" }}
                  </span>
                </span>
              </td>
              <td class="cell-text-center">
                <span>{{ association.email }}</span>
              </td>
              <td class="cell-text-center">
                <span>{{ association.groupName }}</span>
              </td>
              <td class="cell-text-center">
                <span>{{ association.createdByUserName }}</span>
              </td>

              <td>
                <span>{{ formatDateTimeForDisplay(association.addedAt) }}</span>
              </td>
            </tr>

            <tr v-if="isLoadingTable" class="empty-row-state">
              <td colspan="8" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <svg class="empty-spinner" viewBox="0 0 24 24" fill="none">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  <p>Carregando vínculos Pessoa-Grupo...</p>
                </div>
              </td>
            </tr>

            <tr
              v-else-if="associationsToRender.length === 0"
              class="empty-row-state"
            >
              <td colspan="8" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <img src="/img/lupa.webp" alt="" width="38" height="40" class="empty-icon-img" />
                  <p>Nenhum vínculo Pessoa-Grupo encontrado.</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div
          v-if="
            !isLoadingTable && (userGroupStore.totalAssociationsCount ?? 0) > 0
          "
          class="usergroup-pagination-row"
        >
          <div class="usergroup-pagination-left">
            <span class="usergroup-pagination-label">Mostrando</span>
            <PaginationInfo
              :current-page="currentPage"
              :page-size="pageSize"
              :total-count="userGroupStore.totalAssociationsCount ?? 0"
            />
            <span>vínculos</span>
          </div>

          <PaginationButtons
            :current-page="currentPage"
            :page-size="pageSize"
            :total-count="userGroupStore.totalAssociationsCount ?? 0"
            :total-pages="userGroupStore.totalAssociationsPages"
            @change-page="(page) => (currentPage = page)"
          />
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <AddUserGroupForm
      v-if="showAddModal"
      @close="showAddModal = false"
      @refresh="
        handleAssociationsChanged({
          goToFirstPage: true,
          closeAdd: true,
          clearSelection: true,
        })
      "
    />

    <EditUserGroupForm
      v-if="showEditModal"
      :association="userGroupStore.selectedAssociation"
      @close="showEditModal = false"
      @refresh="
        handleAssociationsChanged({ goToFirstPage: false, closeEdit: true })
      "
    />

    <UserGroupCardModal
      v-if="showCardModal"
      :show="showCardModal"
      :user-group-id="selectedUserGroupId"
      @close="showCardModal = false"
      @association-updated="handleAssociationChanged"
      @notify="handleChildNotify"
    />

    <DeleteUserGroupForm
      v-if="showDeleteModal"
      :association="userGroupStore.selectedAssociation"
      @close="showDeleteModal = false"
      @refresh="handleAssociationDeleted"
      @notify="handleChildNotify"
    />

    <BatchDeleteUserGroupForm
      v-if="showBatchDeleteModal"
      :associationIds="userGroupStore.selectedAssociationIds"
      @close="showBatchDeleteModal = false"
      @refresh="handleAssociationDeleted"
      @notify="handleChildNotify"
    />

    <BulkCartesianForm
      v-if="showCartesianModal"
      :mode="cartesianMode"
      @close="showCartesianModal = false"
      @submit="handleCartesianSubmit"
    />

    <UserGroupHelpModal
      v-if="showHelpModal"
      :show="showHelpModal"
      @close="showHelpModal = false"
    />
  </Teleport>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            &times;
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
@media (min-width: 768px) {
  .action-bar {
    flex-direction: row;
    align-items: center;
  }
}

.usergroup-pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem 1rem;
  font-size: 0.82rem;
}
.usergroup-pagination-left {
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.usergroup-pagination-label {
  padding-top: 2px;
}

.empty-state-content {
  width: 100%;
  margin: 0 auto;
}

/* ── Avatar na coluna Nome do Usuário ───────────────────────────────────── */
.user-name-cell {
  display: inline-flex !important;
  align-items: center;
  justify-content: flex-start;
  gap: 0.45rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}
.user-avatar {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background-color: var(--border-color);
  color: var(--text-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex: 0 0 24px;
}
.user-avatar-img {
  width: 100%;
  height: 100%;
  display: block;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}
.user-avatar-icon {
  font-size: 0.72rem;
  line-height: 1;
}
.user-name-text {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>